/**
 * Audit Logs Service
 *
 * API calls for audit log management.
 * @see US-19a.28
 */

import { coreApiClient } from '@/api/core-client';
import type {
  AuditLogListItem,
  AuditLogDetail,
  AuditLogFilters,
  AuditLogListResponse,
} from '@bstock/shared';

const BSTOCK_APP = 'BSTOCK';

// Re-export types for convenience
export type {
  AuditLogListItem,
  AuditLogDetail,
  AuditLogFilters,
  AuditLogListResponse,
};

interface ApiSuccess<T> {
  status: string;
  data: T;
  message?: string;
}

/**
 * Remove empty/undefined values from params to avoid sending empty strings
 */
function cleanParams(params: AuditLogFilters): Partial<AuditLogFilters> {
  const cleaned: Partial<AuditLogFilters> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (cleaned as any)[key] = value;
    }
  }
  return cleaned;
}

export const auditLogsService = {
  /**
   * List audit logs with pagination and filters (scoped to BSTOCK + CORE events)
   */
  async list(params: AuditLogFilters): Promise<AuditLogListResponse> {
    const response = await coreApiClient.get<ApiSuccess<AuditLogListResponse>>('/audit-logs', {
      params: { ...cleanParams(params), app: BSTOCK_APP },
    });
    return response.data.data;
  },

  /**
   * Get a single audit log by ID
   */
  async getById(id: string): Promise<AuditLogDetail> {
    const response = await coreApiClient.get<ApiSuccess<AuditLogDetail>>(`/audit-logs/${id}`);
    return response.data.data;
  },
};
